import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Pressable,
  useColorScheme,
} from 'react-native';
import { Swipeable } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import type { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { ConfirmModal } from '../../components/ConfirmModal';
import { Button } from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { deleteConversationForMe, listChatsForUser } from '../../services/chatService';
import { impactLight } from '../../utils/haptics';
import { getUserProfile } from '../../services/userService';
import type { ChatDoc } from '../../types/models';
import type { MainStackParamList, MainTabParamList } from '../../navigation/types';

type ChatRow = {
  chat: ChatDoc;
  peerName: string;
};

export function MessagesScreen() {
  const insets = useSafeAreaInsets();
  const colorScheme = useColorScheme();
  const { user } = useAuth();
  const tabNavigation = useNavigation<BottomTabNavigationProp<MainTabParamList, 'Messages'>>();
  const [rows, setRows] = useState<ChatRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<{ chatId: string; peerName: string } | null>(
    null,
  );

  const load = useCallback(async () => {
    if (!user?.uid) {
      return;
    }
    setLoading(true);
    try {
      const list = await listChatsForUser(user.uid);
      const enriched: ChatRow[] = [];
      for (const chat of list) {
        const otherId = chat.participants.find(p => p !== user.uid);
        let peerName = 'Annedaş';
        if (otherId) {
          const prof = await getUserProfile(otherId);
          peerName = prof?.displayName ?? `Kullanıcı ${otherId.slice(0, 6)}…`;
        }
        enriched.push({ chat, peerName });
      }
      setRows(enriched);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  useEffect(() => {
    load();
  }, [load]);

  function stackNav() {
    return tabNavigation.getParent<NativeStackNavigationProp<MainStackParamList>>();
  }

  function openChat(chat: ChatDoc, peerName: string) {
    const otherId = chat.participants.find(p => p !== user?.uid) ?? '';
    stackNav()?.navigate('Chat', {
      chatId: chat.id,
      otherUserId: otherId,
      peerName,
    });
  }

  function openDeleteModal(chatId: string, peerName: string) {
    void impactLight();
    setDeleteTarget({ chatId, peerName });
  }

  async function performDeleteConversation() {
    if (!deleteTarget || !user?.uid) {
      return;
    }
    const { chatId } = deleteTarget;
    setDeleteTarget(null);
    try {
      await deleteConversationForMe(chatId, user.uid);
      await load();
    } catch (e) {
      Alert.alert('Hata', e instanceof Error ? e.message : 'Sohbet silinemedi.');
    }
  }

  if (loading) {
    return (
      <View
        className="flex-1 items-center justify-center bg-white dark:bg-black"
        style={{ paddingTop: insets.top }}>
        <ActivityIndicator size="large" color={colorScheme === 'dark' ? '#fafafa' : '#18181b'} />
        <Text className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">Sohbetler yükleniyor…</Text>
      </View>
    );
  }

  return (
    <View className="flex-1 bg-white dark:bg-black">
      <ConfirmModal
        visible={deleteTarget !== null}
        title="Sohbeti sil?"
        message={
          deleteTarget
            ? `"${deleteTarget.peerName}" ile olan sohbet cihazından kaldırılır. Bu işlem geri alınamaz.`
            : ''
        }
        cancelLabel="Vazgeç"
        confirmLabel="Sohbeti sil"
        destructive
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => void performDeleteConversation()}
      />
      <FlatList
        data={rows}
        keyExtractor={r => r.chat.id}
        contentContainerStyle={{
          paddingHorizontal: 24,
          paddingTop: 16 + insets.top,
          paddingBottom: 120 + insets.bottom,
          flexGrow: 1,
        }}
        ListEmptyComponent={
          <View className="mt-6 items-center px-2">
            <View className="mb-6 w-full rounded-2xl border border-zinc-200 bg-white px-8 py-10 dark:border-zinc-800 dark:bg-zinc-900">
              <Text className="mb-2 text-center text-base font-semibold text-zinc-900 dark:text-zinc-100">
                Henüz mesajın yok
              </Text>
              <Text className="mb-8 text-center text-sm leading-6 text-zinc-500 dark:text-zinc-400">
                Hadi bir Annedaş bul!
              </Text>
              <Button onPress={() => tabNavigation.navigate('Search')}>Arama sekmesine git</Button>
            </View>
          </View>
        }
        renderItem={({ item }) => {
          const { chat, peerName } = item;
          const preview = chat.lastMessage?.trim() || 'Henüz mesaj yok';
          const snippet =
            preview.length > 72 ? `${preview.slice(0, 72)}…` : preview;

          return (
            <View className="mb-3">
              <Swipeable
                friction={2}
                overshootRight={false}
                renderRightActions={() => (
                  <View className="flex-row pl-2">
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel="Sohbeti sil"
                      onPress={() => openDeleteModal(chat.id, peerName)}
                      className="w-[76px] items-center justify-center self-stretch rounded-xl bg-red-600 active:opacity-90">
                      <Text className="text-center text-xs font-bold text-white">Sil</Text>
                    </Pressable>
                  </View>
                )}>
                <TouchableOpacity
                  activeOpacity={0.85}
                  delayLongPress={400}
                  onLongPress={() => openDeleteModal(chat.id, peerName)}
                  onPress={() => openChat(chat, peerName)}
                  className="flex-row items-center rounded-xl border border-zinc-200 bg-white px-4 py-3.5 dark:border-zinc-800 dark:bg-zinc-900">
                <View className="min-w-0 flex-1 pr-2">
                  <Text className="font-semibold text-zinc-900 dark:text-zinc-100" numberOfLines={1}>
                    {peerName}
                  </Text>
                  <Text className="mt-1 text-sm text-zinc-500 dark:text-zinc-400" numberOfLines={2}>
                    {snippet}
                  </Text>
                </View>
                <Text className="text-2xl text-zinc-400 dark:text-zinc-500" accessibilityLabel="Oku">
                  ›
                </Text>
              </TouchableOpacity>
              </Swipeable>
            </View>
          );
        }}
      />
    </View>
  );
}
